import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Card,
  Typography,
  Space,
  Divider,
  Grid,
  message,
  Skeleton,
  Tag,
  Tooltip,
  List,
  Avatar,
  Empty,
  Button,
  Modal,
  Alert,
  Switch,
} from "antd";
import {
  CheckOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  LockOutlined,
  ApiOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  DisconnectOutlined,
} from "@ant-design/icons";
import { useAppDispatch } from "../../../hooks/reduxHooks";
import { useAppSelector } from "../../../hooks/reduxHooks";
import { getSociairProfilesApi, createSociairTenantApi, fetchSocialPagesApi, linkSocialMediaProfileApi, deleteSocialMediaProfileApi, updateProfileStatusApi } from "../../../services/socialProfiles.service";
import { sociairIconNameMap, socialIconMap } from "../../../data/sociairIcons";
import UpgradeDialog from "../../../components/common/UpgradeDialog";
import { useSearchParams } from "react-router-dom";
import prettifyString from "../../../utils/prettifyString";

const { Title, Text } = Typography;

const SocialMediaProfileSettingsComponent = () => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(true);
  const [socialMediaProfiles, setSocialMediaProfiles] = useState([]);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showPlatformModal, setShowPlatformModal] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [connectionRequested, setConnectionRequested] = useState(false);
  const [showPagesModal, setShowPagesModal] = useState(false);
  const [socialPages, setSocialPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [currentPlatformType, setCurrentPlatformType] = useState(null);
  const [linkingProfile, setLinkingProfile] = useState(false);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const [profileToUnlink, setProfileToUnlink] = useState(null);
  const [unlinkingProfile, setUnlinkingProfile] = useState(false);
  const [updatingProfiles, setUpdatingProfiles] = useState({});

  const dispatch = useAppDispatch();
  const { isPaidPlan } = useAppSelector((state) => state.currentPlan);
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper function to fetch profiles with polling for Facebook/Instagram
  const fetchProfilesWithPolling = (platformType, retries = 3, delay = 1500) => {
    let attemptCount = 0;

    const fetchProfiles = () => {
      attemptCount++;
      console.log(`Silently fetching profiles (attempt ${attemptCount}/${retries + 1})`);

      dispatch(
        getSociairProfilesApi({
          params: {},
          finalCallback: () => {
            // Do not set loading state - fetch silently
          },
          successCallback: (data) => {
            console.log(`Fetched profiles on attempt ${attemptCount}:`, data);
            setIsConnected(data.is_connected);
            setSocialMediaProfiles(data.profiles || []);

            // Continue polling if we haven't reached max attempts
            if (attemptCount < retries + 1) {
              setTimeout(fetchProfiles, delay);
            }
          },
          failureCallback: (error) => {
            console.error(`Failed to fetch profiles on attempt ${attemptCount}:`, error);

            // Continue polling on failure if we haven't reached max attempts
            if (attemptCount < retries + 1) {
              setTimeout(fetchProfiles, delay);
            } else if (attemptCount >= retries + 1) {
              // Only show error on final attempt
              message.error("Failed to load social media profiles");
            }
          },
        })
      );
    };

    // Start the first fetch
    fetchProfiles();
  };

  useEffect(() => {
    dispatch(
      getSociairProfilesApi({
        params: {},
        finalCallback: () => setLoading(false),
        successCallback: (data) => {
          console.log('Social media API response:', data);
          // Always use data.profiles for the profile list
          setIsConnected(data.is_connected);
          setSocialMediaProfiles(data.profiles || []);
        },
        failureCallback: (error) => {
          console.error('Failed to load profiles:', error);
          setIsConnected(false);
          setSocialMediaProfiles([]);
          message.error("Failed to load social media profiles");
        },
      })
    );
  }, [dispatch]);

  useEffect(() => {
    const callback = searchParams.get("callback");
    const token = searchParams.get("token");
    if (callback && token) {
      // Handle OAuth callback
      console.log('OAuth callback detected:', callback, token);

      // Store the platform type for later use when linking
      setCurrentPlatformType(callback);

      // For TikTok and Instagram Business, directly link the profile without fetching pages
      if (callback === 'tiktok' || callback === 'instagram_business') {
        setLinkingProfile(true);
        dispatch(
          linkSocialMediaProfileApi({
            socialType: callback,
            data: {
              code: token,
            },
            finalCallback: () => {
              setLinkingProfile(false);
              // Remove the params from URL after linking completes
              searchParams.delete("callback");
              searchParams.delete("token");
              setSearchParams(searchParams, { replace: true });
            },
            successCallback: (response) => {
              console.log(`${callback} link response:`, response);
              if (response.error) {
                message.error(response.error);
                return;
              }

              // Check if response contains a message indicating async linking
              const isAsyncLinking = response.data?.message &&
                response.data.message.toLowerCase().includes('being linked');

              // Show tooltip message from response body
              if (response.data?.message) {
                message.info(response.data.message, 5); // Show for 5 seconds
              } else {
                message.success(`Successfully connected ${getPlatformName(callback)} profile`);
              }

              // Refresh profiles - use polling for TikTok and Instagram Business
              // For TikTok and Instagram Business, use silent polling (3 retries, 2 seconds apart)
              if (callback === 'tiktok' || callback === 'instagram_business') {
                fetchProfilesWithPolling(callback, 3, 2000);
              } else {
                // For other platforms, single fetch without full page loading
                dispatch(
                  getSociairProfilesApi({
                    params: {},
                    finalCallback: () => { },
                    successCallback: (data) => {
                      console.log(`Fetched profiles after ${callback} link:`, data);
                      setIsConnected(data.is_connected);
                      setSocialMediaProfiles(data.profiles || []);
                    },
                    failureCallback: (error) => {
                      console.error(`Failed to fetch profiles after ${callback} link:`, error);
                      message.error("Failed to load social media profiles");
                    },
                  })
                );
              }
            },
            failureCallback: (error) => {
              console.error(`Failed to link ${callback} profile:`, error);
              message.error(error?.response?.data?.detail || error?.message || `Failed to connect ${getPlatformName(callback)} profile`);
            },
          })
        );
      } else {
        // For other platforms (facebook, instagram, etc.), fetch social pages
        // Show modal immediately and start loading
        setShowPagesModal(true);
        setPagesLoading(true);
        setSocialPages([]);

        dispatch(
          fetchSocialPagesApi({
            data: {
              profile_type: callback,
              callback_token: token,
            },
            finalCallback: () => {
              setPagesLoading(false);
              // Remove the params from URL after fetch completes
              searchParams.delete("callback");
              searchParams.delete("token");
              setSearchParams(searchParams, { replace: true });
            },
            successCallback: (data) => {
              console.log('Fetched social pages:', data);
              if (data.is_connected && data.pages && data.pages.length > 0) {
                setSocialPages(data.pages);
                message.success(`Found ${data.pages.length} ${callback} page${data.pages.length > 1 ? 's' : ''}`);
                // Keep modal open to show pages
              } else if (data.error) {
                message.error(data.error);
                // Keep modal open to show error state
                setSocialPages([]);
              } else {
                message.warning("No pages found");
                // Keep modal open to show empty state
                setSocialPages([]);
              }
            },
            failureCallback: (error) => {
              console.error('Failed to fetch social pages:', error);
              message.error(error?.message || "Failed to fetch social pages");
              // Keep modal open to show error state
              setSocialPages([]);
            },
          })
        );
      }
    }
  }, [searchParams, setSearchParams, dispatch]);

  const getPlatformIcon = (profileType) => {
    const iconName = socialIconMap[profileType] || "logos:meta-icon";
    return (
      <iconify-icon
        icon={iconName}
        width="18"
        height="18"
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      ></iconify-icon>
    );
  };

  const getPlatformName = (profileType) =>
    sociairIconNameMap[profileType] || "Social Media";

  const getExpiryText = (profile) => {
    try {
      if (profile && profile.expiry_date) {
        const diff = dayjs(profile.expiry_date).startOf('day').diff(dayjs().startOf('day'), 'day');
        if (diff > 0) return `Expires in ${diff} day${diff === 1 ? '' : 's'}`;
        if (diff === 0) return 'Expires today';
        return `Expired ${Math.abs(diff)} day${Math.abs(diff) === 1 ? '' : 's'} ago`;
      }

      if (profile && typeof profile.remaining_days === 'number') {
        const rd = profile.remaining_days;
        if (rd > 0) return `Expires in ${rd} day${rd === 1 ? '' : 's'}`;
        if (rd === 0) return 'Expires today';
        return `Expired ${Math.abs(rd)} day${Math.abs(rd) === 1 ? '' : 's'} ago`;
      }

      return '';
    } catch (e) {
      return '';
    }
  };

  const handleAddSocialMedia = () => {
    if (!isPaidPlan) {
      setShowUpgradeDialog(true);
    } else {
      if (isConnected) {
        setShowPlatformModal(true);
      } else {
        setShowRequestModal(true);
      }
    }
  };

  const handleRelink = (profile) => {
    if (profile.relink_url) {
      window.location.href = profile.relink_url;
    }
  };

  const handleUnlink = (profile) => {
    setProfileToUnlink(profile);
    setShowUnlinkModal(true);
  };

  const confirmUnlink = () => {
    if (!profileToUnlink) return;

    setUnlinkingProfile(true);
    dispatch(
      deleteSocialMediaProfileApi({
        data: {
          delete_rows: [profileToUnlink.id]
        },
        finalCallback: () => setUnlinkingProfile(false),
        successCallback: (response) => {
          console.log('Delete response:', response);
          message.success(`Successfully deleted ${profileToUnlink.name}`);
          setShowUnlinkModal(false);
          setProfileToUnlink(null);

          // Refresh profiles
          dispatch(
            getSociairProfilesApi({
              params: {},
              finalCallback: () => { },
              successCallback: (data) => {
                console.log('Fetched profiles after delete:', data);
                setIsConnected(data.is_connected);
                setSocialMediaProfiles(data.profiles || []);
              },
              failureCallback: (error) => {
                console.error('Failed to fetch profiles after delete:', error);
              },
            })
          );
        },
        failureCallback: (error) => {
          console.error('Failed to delete profile:', error);
          message.error(error?.response?.data?.detail || error?.message || 'Failed to delete social media profile');
        },
      })
    );
  };

  const handleStatusChange = (checked, profile) => {
    const newStatus = checked ? 'enabled' : 'disabled';

    setUpdatingProfiles(prev => ({ ...prev, [profile.id]: true }));

    dispatch(
      updateProfileStatusApi({
        profileId: profile.id,
        status: newStatus,
        finalCallback: () => {
          setUpdatingProfiles(prev => {
            const newState = { ...prev };
            delete newState[profile.id];
            return newState;
          });
        },
        successCallback: (data) => {
          setSocialMediaProfiles(prevProfiles =>
            prevProfiles.map(p =>
              p.id === profile.id ? { ...p, ai_configure: checked ? 2 : 0 } : p
            )
          );
          message.success(`AI configuration ${checked ? 'enabled' : 'disabled'} for ${profile.name}`);
        },
        failureCallback: (error) => {
          message.error("Failed to update status");
        }
      })
    );
  };

  const handleRequestConnection = () => {
    setRequestLoading(true);
    dispatch(
      createSociairTenantApi({
        data: {},
        finalCallback: () => setRequestLoading(false),
        successCallback: () => {
          setConnectionRequested(true);
          message.success("Connection request submitted successfully");
        },
        failureCallback: (error) => {
          message.error(error?.message || "Failed to submit connection request");
        },
      })
    );
  };

  const handlePlatformSelect = (platform) => {
    const platformUrls = {
      facebook: `https://connect.sociair.com/facebook?url=${encodeURIComponent(window.location.href)}`,
      instagram: `https://connect.sociair.com/instagram?url=${encodeURIComponent(window.location.href)}`,
      instagram_business: `https://connect.sociair.com/instagram_business?url=${encodeURIComponent(window.location.href)}`,
      whatsapp: `https://connect.sociair.com/whatsapp?url=${encodeURIComponent(window.location.href)}`,
      tiktok: `https://connect.sociair.com/tiktok?url=${encodeURIComponent(window.location.href)}`,
    };

    const url = platformUrls[platform];
    if (url) {
      // window.open(url, "_blank", "noopener,noreferrer");
      window.location.href = url;
      message.success(`Redirected to ${prettifyString(platform)} connection page`);
    }
    setShowPlatformModal(false);
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const platformOptions = [
    {
      key: "facebook",
      name: "Facebook",
      description: "Connect your Facebook pages",
      icon: "logos:facebook",
      tone: { bg: "#eef2ff", border: "#d6defa", accent: "#3b82f6" },
    },
    {
      key: "instagram",
      name: "Instagram (Linked with Facebook)",
      description: "Connect Instagram accounts linked to Facebook",
      icon: "skill-icons:instagram",
      tone: { bg: "#fff1f2", border: "#ffd6df", accent: "#e11d48" },
    },
    {
      key: "instagram_business",
      name: "Instagram Business",
      description: "Connect Instagram Business accounts",
      icon: "skill-icons:instagram",
      tone: { bg: "#fff1f2", border: "#ffd6df", accent: "#e11d48" },
    },
    {
      key: "whatsapp",
      name: "WhatsApp",
      description: "Connect your WhatsApp Business account",
      icon: "logos:whatsapp-icon",
      tone: { bg: "#ecfdf5", border: "#c7f1dd", accent: "#10b981" },
    },
    {
      key: "tiktok",
      name: "TikTok",
      description: "Connect your TikTok account",
      icon: "logos:tiktok-icon",
      tone: { bg: "#f5f3ff", border: "#e0d7ff", accent: "#7c3aed" },
    },
  ];

  const activeProfiles = socialMediaProfiles.filter(
    (p) => !p.is_expired && !p.about_to_expire
  );
  const expiringSoonProfiles = socialMediaProfiles.filter(
    (p) => p.about_to_expire && !p.is_expired
  );
  const expiredProfiles = socialMediaProfiles.filter((p) => p.is_expired);

  if (loading) {
    return (
      <div className="ai-setup-component">
        <Card style={{ width: "100%", borderRadius: 8 }}>
          <Skeleton active />
        </Card>
      </div>
    );
  }

  return (
    <div className="ai-setup-component">
      <Card
        style={{
          width: "100%",
          borderRadius: 12,
          boxShadow: "0 6px 16px rgba(15, 23, 42, 0.08)",
        }}
        bodyStyle={{
          padding: isMobile ? 16 : 24,
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            flexDirection: isMobile ? "column" : "row",
            gap: 16,
            padding: isMobile ? "16px" : "20px 24px",
            background: "linear-gradient(135deg, #f7f8fc 0%, #eef5ff 100%)",
            borderRadius: 14,
            border: "1px solid #e7edf6",
            marginBottom: 20,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Title level={4} style={{ marginBottom: 4, marginTop: 0 }}>
              Social Media Profile Settings
            </Title>
            <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
              Connect and manage your social media profiles for automated responses
            </Typography.Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {socialMediaProfiles.length} connected profile{socialMediaProfiles.length === 1 ? "" : "s"}
            </Text>
          </div>
          <div style={{ display: "flex", alignItems: "center", height: "100%", width: isMobile ? "100%" : "auto" }}>
            <Button
              type="primary"
              icon={isPaidPlan ? <PlusOutlined /> : <LockOutlined />}
              onClick={handleAddSocialMedia}
              style={{
                width: isMobile ? "100%" : "auto",
                borderRadius: 10,
                height: 40,
              }}
            >
              Add Social Media
            </Button>
          </div>
        </div>

        <Divider style={{ margin: "0 0 20px 0" }} />

        {/* Show alert if not connected */}
        {/* {!isConnected && (
          <Alert
            message="Social Media Connection Required"
            description="Your account needs social media integration to be set up. Click 'Add Social Media' to request access."
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: "24px" }}
          />
        )} */}

        {/* Stats Cards */}
        {/* <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Card size="small" style={{ textAlign: "center", background: "#f8f9fa" }}>
                <Text strong style={{ color: "#52c41a" }}>
                  {activeProfiles.length}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>Active</Text>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" style={{ textAlign: "center", background: "#f8f9fa" }}>
                <Text strong style={{ color: "#fa8c16" }}>
                  {expiringSoonProfiles.length}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>Expiring Soon</Text>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" style={{ textAlign: "center", background: "#f8f9fa" }}>
                <Text strong style={{ color: "#ff4d4f" }}>
                  {expiredProfiles.length}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>Expired</Text>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" style={{ textAlign: "center", background: "#f8f9fa" }}>
                <Text strong style={{ color: "#1890ff" }}>
                  {socialMediaProfiles.length}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>Total</Text>
              </Card>
            </Col>
          </Row>
        </div> */}

        {/* Profiles List */}
        {socialMediaProfiles.length > 0 ? (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            {socialMediaProfiles.map((profile) => {
              const isExpired = profile.is_expired || profile.remaining_days === 0;
              const isExpiringSoon = profile.about_to_expire && !profile.is_expired;

              return (
                <Card
                  key={profile.id}
                  style={{
                    borderRadius: 14,
                    boxShadow: "0 10px 26px rgba(15, 23, 42, 0.08)",
                    border: "1px solid #eef2f7",
                    transition: "all 0.25s",
                  }}
                  bodyStyle={{ padding: isMobile ? 16 : 20 }}
                  hoverable
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      {isMobile ? (
                        <>
                          <Space size="middle" align="center" style={{ minWidth: 0 }}>
                            <div style={{ position: "relative" }}>
                              <Avatar size={52} src={profile.photo} style={{ border: "3px solid #f0f0f0" }} />
                              <Tooltip title={getPlatformName(profile.profile_type)}>
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: -4,
                                    right: -4,
                                    backgroundColor: "white",
                                    borderRadius: "50%",
                                    padding: 3,
                                    border: "2px solid #f0f0f0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 24,
                                    height: 24,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  {getPlatformIcon(profile.profile_type)}
                                </div>
                              </Tooltip>
                            </div>
                            <div style={{ minWidth: 0, width: "100%" }}>
                              <Text
                                strong
                                style={{
                                  fontSize: 15,
                                  display: "block",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "70%",
                                  marginBottom: 4,
                                }}
                              >
                                {profile.name}
                              </Text>
                              <Tooltip title={`Expiry date: ${formatDate(profile.expiry_date)}`}>
                                <Tag
                                  color={isExpired ? "red" : isExpiringSoon ? "orange" : "green"}
                                  style={{
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    padding: "0 10px",
                                    lineHeight: "20px",
                                    margin: 0,
                                  }}
                                >
                                  {getExpiryText(profile)}
                                </Tag>
                              </Tooltip>
                            </div>
                          </Space>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
                            <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                              AI Reply
                            </Text>
                            <Tooltip title="AI Reply">
                              <Switch
                                checked={profile.ai_configure !== 0}
                                onChange={(checked) => handleStatusChange(checked, profile)}
                                loading={updatingProfiles[profile.id]}
                                disabled={isExpired}
                                size="small"
                              />
                            </Tooltip>
                          </div>
                        </>
                      ) : (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "minmax(220px, 1fr) 180px 140px",
                            alignItems: "center",
                            columnGap: 24,
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          <Space size="middle" align="center" style={{ minWidth: 0 }}>
                            <div style={{ position: "relative" }}>
                              <Avatar size={52} src={profile.photo} style={{ border: "3px solid #f0f0f0" }} />
                              <Tooltip title={getPlatformName(profile.profile_type)}>
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: -4,
                                    right: -4,
                                    backgroundColor: "white",
                                    borderRadius: "50%",
                                    padding: 3,
                                    border: "2px solid #f0f0f0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 24,
                                    height: 24,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  {getPlatformIcon(profile.profile_type)}
                                </div>
                              </Tooltip>
                            </div>
                            <Text
                              strong
                              style={{
                                fontSize: 15,
                                display: "block",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 220,
                              }}
                            >
                              {profile.name}
                            </Text>
                          </Space>
                          <div>
                            <Tooltip title={`Expiry date: ${formatDate(profile.expiry_date)}`}>
                              <Tag
                                color={isExpired ? "red" : isExpiringSoon ? "orange" : "green"}
                                style={{
                                  borderRadius: 999,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  padding: "0 10px",
                                  lineHeight: "20px",
                                  margin: 0,
                                }}
                              >
                                {getExpiryText(profile)}
                              </Tag>
                            </Tooltip>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                              AI Reply
                            </Text>
                            <Tooltip title="AI Reply">
                              <Switch
                                checked={profile.ai_configure !== 0}
                                onChange={(checked) => handleStatusChange(checked, profile)}
                                loading={updatingProfiles[profile.id]}
                                disabled={isExpired}
                                size="small"
                              />
                            </Tooltip>
                          </div>
                        </div>
                      )}

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          width: isMobile ? "100%" : "auto",
                        }}
                      >
                        <Tooltip title={!profile.relink_url ? "Relink not supported for this profile" : "Relink this social media profile to refresh permissions"}>
                          <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={() => handleRelink(profile)}
                            disabled={!profile.relink_url}
                            style={{
                              background: !profile.relink_url ? "#d9d9d9" : (isExpired ? "#ff4d4f" : isExpiringSoon ? "#fa8c16" : "#1890ff"),
                              borderColor: !profile.relink_url ? "#d9d9d9" : (isExpired ? "#ff4d4f" : isExpiringSoon ? "#fa8c16" : "#1890ff"),
                              color: !profile.relink_url ? "#8c8c8c" : undefined,
                              borderRadius: 8,
                              fontWeight: 500,
                              height: 36,
                              flex: isMobile ? "1 1 140px" : "0 0 auto",
                              // visibility: !profile.relink_url ? "hidden" : "inline-flex",
                            }}
                          >
                            Relink
                          </Button>
                        </Tooltip>
                        <Tooltip title="Disconnect this social media profile">
                          <Button
                            danger
                            icon={<DisconnectOutlined />}
                            onClick={() => handleUnlink(profile)}
                            style={{
                              borderRadius: 8,
                              fontWeight: 500,
                              height: 36,
                              flex: isMobile ? "1 1 140px" : "0 0 auto",
                            }}
                          >
                            Unlink
                          </Button>
                        </Tooltip>
                      </div>
                    </div>

                    <div style={{ height: 0 }} />
                  </div>
                </Card>
              );
            })}
          </Space>
        ) : (
          <Empty
            description={
              <Space direction="vertical" size="small" style={{ marginTop: 40, marginBottom: 40 }}>
                <Typography.Text type="secondary" style={{ fontSize: 15 }}>
                  No social media profiles connected yet.
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Connect your social media accounts to enable automated responses and monitoring.
                </Typography.Text>
              </Space>
            }
            style={{ padding: "48px 0" }}
          />
        )}

        {/* Request Connection Modal */}
        <Modal
          title={
            <Space>
              <ApiOutlined style={{ color: "#1890ff" }} />
              <span>Request Social Media Connection</span>
            </Space>
          }
          open={showRequestModal}
          onCancel={() => {
            setShowRequestModal(false);
            setConnectionRequested(false);
          }}
          footer={null}
          centered
          width={500}
        >
          {connectionRequested ? (
            <Space direction="vertical" size="large" style={{ width: "100%", textAlign: "center" }}>
              <div style={{ fontSize: "48px", color: "#52c41a" }}>
                <CheckOutlined />
              </div>
              <div>
                <Title level={4} style={{ color: "#52c41a", marginBottom: "8px" }}>
                  Request Submitted Successfully!
                </Title>
                <Alert
                  message="Connection Request Pending"
                  description={
                    <div>
                      <p style={{ marginBottom: "8px" }}>
                        Your social media connection request is pending. Please check back shortly.
                      </p>
                      <p style={{ marginBottom: "0", color: "#8c8c8c" }}>
                        <InfoCircleOutlined /> This process typically takes 2-5 minutes.
                      </p>
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ textAlign: "left" }}
                />
              </div>
              <Button
                type="primary"
                onClick={() => {
                  setShowRequestModal(false);
                  setConnectionRequested(false);
                  // Refresh profiles
                  setLoading(true);
                  dispatch(
                    getSociairProfilesApi({
                      params: {},
                      finalCallback: () => setLoading(false),
                      successCallback: (data) => {
                        console.log('Refreshed social media API response:', data);
                        setIsConnected(data.is_connected);
                        setSocialMediaProfiles(data.profiles || []);
                      },
                      failureCallback: (error) => {
                        console.error('Failed to refresh profiles:', error);
                        setIsConnected(false);
                        setSocialMediaProfiles([]);
                        message.error("Failed to load social media profiles");
                      },
                    })
                  );
                }}
              >
                Close and Refresh
              </Button>
            </Space>
          ) : (
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Alert
                message="Social Media Connection Not Available"
                description="Your account doesn't have social media integration enabled yet. Click the button below to request access."
                type="warning"
                showIcon
              />

              <div style={{ textAlign: "center" }}>
                {/* <Text type="secondary" style={{ display: "block", marginBottom: "16px" }}>
                  Once approved, you'll be able to connect your social media profiles and enable automated responses.
                </Text> */}
                <Button
                  type="primary"
                  size="large"
                  icon={<ApiOutlined />}
                  onClick={handleRequestConnection}
                  loading={requestLoading}
                  block
                >
                  Request Social Media Connection
                </Button>
                <Text type="secondary" style={{ display: "block", marginTop: "12px", fontSize: "12px" }}>
                  <InfoCircleOutlined /> Connection setup typically takes 2-5 minutes
                </Text>
              </div>
            </Space>
          )}
        </Modal>

        {/* Platform Selection Modal */}
        <Modal
          title={
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: "6px",
                marginBottom: "12px",
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 600, color: "#0f172a" }}>
                Choose Social Media Platform
              </div>
              <div style={{ fontSize: 13, color: "#64748b", fontWeight: 400 }}>
                Select a platform to connect your account. You will be redirected to authorize access.
              </div>
            </div>
          }
          open={showPlatformModal}
          onCancel={() => setShowPlatformModal(false)}
          footer={null}
          centered
          width={600}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {platformOptions.map((option) => (
              <Card
                key={option.key}
                hoverable
                onClick={() => handlePlatformSelect(option.key)}
                style={{
                  cursor: "pointer",
                  borderRadius: 12,
                  border: `1px solid ${option.tone.border}`,
                  background: "#ffffff",
                  transition: "all 0.2s ease",
                }}
                bodyStyle={{ padding: "14px 16px" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 46,
                      height: 46,
                      borderRadius: 12,
                      background: option.tone.bg,
                      border: `1px solid ${option.tone.border}`,
                    }}
                  >
                    <iconify-icon icon={option.icon} width="28" height="28"></iconify-icon>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ fontSize: 15, display: "block" }}>
                      {option.name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {option.description}
                    </Text>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: option.tone.accent,
                      border: `1px solid ${option.tone.border}`,
                      borderRadius: 999,
                      padding: "2px 10px",
                      background: option.tone.bg,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Connect
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Modal>

        {/* Social Pages Selection Modal */}
        <Modal
          title={
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "8px 0"
            }}>
              {currentPlatformType && (
                <iconify-icon
                  icon={socialIconMap[currentPlatformType] || "logos:meta-icon"}
                  width="32"
                  height="32"
                ></iconify-icon>
              )}
              <div>
                <div style={{ fontSize: "18px", fontWeight: "600", color: "#262626" }}>
                  Connect {currentPlatformType ? getPlatformName(currentPlatformType) : ''} Page
                </div>
                <div style={{ fontSize: "13px", fontWeight: "400", color: "#8e8e8e", marginTop: "2px" }}>
                  Select the page you want to connect
                </div>
              </div>
            </div>
          }
          open={showPagesModal}
          onCancel={() => {
            setShowPagesModal(false);
            setSocialPages([]);
            setSelectedPage(null);
            setCurrentPlatformType(null);
          }}
          footer={
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "16px 0 8px"
            }}>
              <Button
                type="primary"
                size="large"
                disabled={!selectedPage}
                loading={linkingProfile}
                icon={<ApiOutlined />}
                style={{
                  height: "44px",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "500",
                  minWidth: "180px"
                }}
                onClick={() => {
                  if (!selectedPage || !currentPlatformType) return;

                  // Build the payload based on social media type
                  let payload = {};

                  if (currentPlatformType === 'tiktok') {
                    // TikTok only needs the code
                    payload = {
                      code: selectedPage.code || selectedPage.id,
                    };
                  } else if (currentPlatformType === 'facebook') {
                    // Facebook payload format
                    payload = {
                      picture: selectedPage.picture,
                      id: selectedPage.id,
                      name: selectedPage.name,
                      category: selectedPage.category,
                      access_token: selectedPage.access_token,
                      fan_count: selectedPage.fan_count,
                      followers_count: selectedPage.followers_count,
                      talking_about_count: selectedPage.talking_about_count,
                      is_relink: selectedPage.is_relink || false,
                    };
                  } else if (currentPlatformType === 'instagram') {
                    // Instagram payload format (includes connected_instagram_account and instagram_business_account)
                    payload = {
                      picture: selectedPage.picture,
                      id: selectedPage.id,
                      name: selectedPage.name,
                      category: selectedPage.category,
                      access_token: selectedPage.access_token,
                      fan_count: selectedPage.fan_count,
                      followers_count: selectedPage.followers_count,
                      talking_about_count: selectedPage.talking_about_count,
                      connected_instagram_account: selectedPage.connected_instagram_account,
                      instagram_business_account: selectedPage.instagram_business_account,
                      is_relink: selectedPage.is_relink || false,
                    };
                  } else {
                    // Default/generic payload - send the whole selected page object
                    payload = {
                      ...selectedPage,
                      is_relink: selectedPage.is_relink || false,
                    };
                  }

                  setLinkingProfile(true);
                  dispatch(
                    linkSocialMediaProfileApi({
                      socialType: currentPlatformType,
                      data: payload,
                      finalCallback: () => setLinkingProfile(false),
                      successCallback: (response) => {
                        console.log('Page link response:', response);
                        if (response.error) {
                          message.error(response.error);
                          return;
                        }

                        // Show tooltip message from response body
                        if (response.data?.message) {
                          message.info(response.data.message, 5); // Show for 5 seconds
                        } else {
                          message.success(`Successfully connected ${selectedPage?.name}`);
                        }

                        setShowPagesModal(false);
                        setSocialPages([]);
                        setSelectedPage(null);

                        // Refresh profiles with silent polling for Facebook and Instagram
                        // Use silent polling for Facebook and Instagram (3 retries, 2 seconds apart)
                        if (currentPlatformType === 'facebook' || currentPlatformType === 'instagram') {
                          const platformTypeCopy = currentPlatformType;
                          setCurrentPlatformType(null);
                          fetchProfilesWithPolling(platformTypeCopy, 3, 2000);
                        } else {
                          setCurrentPlatformType(null);
                          // For other platforms, single fetch without full page loading
                          dispatch(
                            getSociairProfilesApi({
                              params: {},
                              finalCallback: () => { },
                              successCallback: (data) => {
                                console.log('Fetched profiles after page link:', data);
                                setIsConnected(data.is_connected);
                                setSocialMediaProfiles(data.profiles || []);
                              },
                              failureCallback: (error) => {
                                console.error('Failed to fetch profiles after page link:', error);
                                message.error("Failed to load social media profiles");
                              },
                            })
                          );
                        }
                      },
                      failureCallback: (error) => {
                        console.error('Failed to link profile:', error);
                        message.error(error?.response?.data?.detail || error?.message || "Failed to connect social media profile");
                      },
                    })
                  );
                }}
              >
                Connect Page
              </Button>
            </div>
          }
          centered
          width={560}
          bodyStyle={{ padding: "24px" }}
          closeIcon={
            <iconify-icon
              icon="ic:round-close"
              width="20"
              height="20"
              style={{ color: "#8e8e8e" }}
            ></iconify-icon>
          }
        >
          {pagesLoading ? (
            <div style={{ textAlign: "center", padding: "48px 0" }}>
              <iconify-icon
                icon="eos-icons:loading"
                width="48"
                height="48"
                style={{ color: "#1890ff" }}
              ></iconify-icon>
              <Typography.Text type="secondary" style={{ display: "block", marginTop: "20px", fontSize: "14px" }}>
                Fetching your {currentPlatformType ? getPlatformName(currentPlatformType) : ''} pages...
              </Typography.Text>
            </div>
          ) : socialPages.length > 0 ? (
            <div style={{ marginTop: "8px" }}>
              <List
                dataSource={socialPages}
                renderItem={(page) => {
                  const isSelected = selectedPage?.id === page.id;
                  return (
                    <List.Item
                      onClick={() => setSelectedPage(page)}
                      style={{
                        cursor: "pointer",
                        padding: "16px 18px",
                        marginBottom: "12px",
                        borderRadius: "12px",
                        border: isSelected
                          ? '2px solid #1890ff'
                          : "2px solid transparent",
                        background: isSelected
                          ? '#e6f7ff'
                          : "#fafafa",
                        boxShadow: isSelected
                          ? '0 4px 12px rgba(24, 144, 255, 0.15)'
                          : "0 2px 8px rgba(0,0,0,0.04)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: "relative"
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)";
                        }
                      }}
                    >
                      <List.Item.Meta
                        avatar={
                          <div style={{ position: "relative" }}>
                            <Avatar
                              size={60}
                              src={page.picture?.data?.url}
                              style={{
                                border: isSelected
                                  ? '3px solid #1890ff'
                                  : "3px solid #e8e8e8",
                                transition: "all 0.3s"
                              }}
                            />
                            {isSelected && (
                              <div style={{
                                position: "absolute",
                                bottom: "-4px",
                                right: "-4px",
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                                background: '#1890ff',
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "2px solid white",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                              }}>
                                <CheckOutlined style={{ color: "#fff", fontSize: "12px" }} />
                              </div>
                            )}
                          </div>
                        }
                        title={
                          <div style={{
                            fontSize: "16px",
                            fontWeight: isSelected ? "600" : "500",
                            color: "#262626",
                            marginBottom: "4px"
                          }}>
                            {page.name}
                          </div>
                        }
                        description={
                          <Space direction="vertical" size={6} style={{ width: "100%" }}>
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              color: "#8e8e8e",
                              fontSize: "13px"
                            }}>
                              <iconify-icon
                                icon="mdi:tag-outline"
                                width="14"
                                height="14"
                              ></iconify-icon>
                              {page.category}
                            </div>
                            {page.fan_count !== undefined && (
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                color: "#595959",
                                fontSize: "13px",
                                fontWeight: "500"
                              }}>
                                <iconify-icon
                                  icon="mdi:account-group-outline"
                                  width="15"
                                  height="15"
                                ></iconify-icon>
                                {page.fan_count.toLocaleString()} followers
                              </div>
                            )}
                            {page.is_relink && (
                              <Tag
                                color="orange"
                                style={{
                                  marginTop: "4px",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                  padding: "2px 8px"
                                }}
                              >
                                Relink Page
                              </Tag>
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </div>
          ) : (
            <Empty
              image={
                <iconify-icon
                  icon="mdi:page-layout-sidebar-left"
                  width="64"
                  height="64"
                  style={{ color: "#d9d9d9" }}
                ></iconify-icon>
              }
              description={
                <Space direction="vertical" size="small" style={{ marginTop: "8px" }}>
                  <Typography.Text style={{ fontSize: "15px", color: "#595959", fontWeight: "500" }}>
                    No pages found
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: "13px" }}>
                    Make sure you have admin access to at least one page
                  </Typography.Text>
                </Space>
              }
              style={{ padding: "48px 0" }}
            />
          )}
        </Modal>

        <UpgradeDialog
          visible={showUpgradeDialog}
          onClose={() => setShowUpgradeDialog(false)}
          feature="Adding social media profiles"
        />

        {/* Unlink Confirmation Modal */}
        <Modal
          title={
            <Space>
              <ExclamationCircleOutlined style={{ color: "#ff4d4f", fontSize: 20 }} />
              <span>Disconnect Social Media Profile</span>
            </Space>
          }
          open={showUnlinkModal}
          onCancel={() => {
            setShowUnlinkModal(false);
            setProfileToUnlink(null);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setShowUnlinkModal(false);
                setProfileToUnlink(null);
              }}
              disabled={unlinkingProfile}
            >
              Cancel
            </Button>,
            <Button
              key="unlink"
              type="primary"
              danger
              loading={unlinkingProfile}
              onClick={confirmUnlink}
              icon={<DisconnectOutlined />}
            >
              Unlink
            </Button>,
          ]}
          centered
          width={500}
        >
          {profileToUnlink && (
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Alert
                message="Warning"
                description="This action will disconnect your social media profile. You will need to reconnect it again to continue using automated responses."
                type="warning"
                showIcon
              />

              <div
                style={{
                  padding: "16px",
                  background: "#fafafa",
                  borderRadius: 8,
                  border: "1px solid #f0f0f0",
                }}
              >
                <Space size="middle" align="center">
                  <div style={{ position: "relative" }}>
                    <Avatar size={56} src={profileToUnlink.photo} />
                    <div
                      style={{
                        position: "absolute",
                        bottom: -2,
                        right: -2,
                        backgroundColor: "white",
                        borderRadius: "50%",
                        padding: 3,
                        border: "2px solid #f0f0f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 24,
                        height: 24,
                      }}
                    >
                      {getPlatformIcon(profileToUnlink.profile_type)}
                    </div>
                  </div>
                  <div>
                    <Text strong style={{ fontSize: 15, display: "block" }}>
                      {profileToUnlink.name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {getPlatformName(profileToUnlink.profile_type)}
                    </Text>
                  </div>
                </Space>
              </div>

              <Text type="secondary" style={{ fontSize: 13 }}>
                Are you sure you want to disconnect this profile?
              </Text>
            </Space>
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default SocialMediaProfileSettingsComponent;
